import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";

interface InitializationStatusSectionProps {
  status: string | undefined;
  loading: boolean;
  generatedAt: string;
  error: string | undefined;
  phase: string | undefined;
  progress: number | undefined;
  onInitialize: () => void;
  onReinitialize: () => void;
  isInitializing?: boolean;
  isReinitializing?: boolean;
  isOwner: boolean;
}

interface OwnerActionsProps {
  isOwner: boolean;
  helperText: ReactNode;
  children: ReactNode;
}

const OVERVIEW_BLURB =
  "An AI-generated overview of this dataset's tables and their contents, used by the AI agents to navigate the dataset.";

const OwnerActions = ({ isOwner, helperText, children }: OwnerActionsProps) => {
  if (!isOwner) {
    return null;
  }

  return (
    <>
      <p className="text-sm text-muted-foreground">{helperText}</p>
      <div className="flex gap-2">{children}</div>
    </>
  );
};

const InitializationStatusSection = ({
  status,
  loading,
  generatedAt,
  error,
  phase,
  progress,
  onInitialize,
  onReinitialize,
  isInitializing,
  isReinitializing,
  isOwner,
}: InitializationStatusSectionProps) => {
  if (loading && !status) {
    return null;
  }

  if (status === "running") {
    const progressPercent = typeof progress === "number" ? Math.round(progress * 100) : null;
    const progressLabel =
      phase && progressPercent !== null
        ? `${phase} — ${progressPercent}%`
        : (phase ?? "Dataset overview is being generated...");

    return (
      <div className="flex flex-col gap-1.5">
        <p className="text-sm text-muted-foreground">{OVERVIEW_BLURB}</p>
        <div className="flex items-center gap-1.5">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{progressLabel}</p>
        </div>
        {progressPercent !== null && (
          <div
            className="h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuenow={progressPercent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="h-full bg-primary transition-[width] duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}
      </div>
    );
  }

  if (status === "completed") {
    return (
      <div className="flex flex-col gap-1.5">
        <p className="text-sm text-muted-foreground">
          {OVERVIEW_BLURB} <strong>The overview is READ ONLY.</strong>
        </p>
        <p className="text-sm text-muted-foreground">
          Dataset overview last generated: {generatedAt}
        </p>
        <OwnerActions
          isOwner={isOwner}
          helperText={
            <>
              Choose <strong>Update</strong> to incorporate newly added tables or resume an
              interrupted run, or <strong>Reinitialize</strong> to rebuild the overview from
              scratch.
            </>
          }
        >
          <Button variant="outline" size="sm" loading={isInitializing} onClick={onInitialize}>
            Update
          </Button>
          <Button variant="secondary" size="sm" loading={isReinitializing} onClick={onReinitialize}>
            Reinitialize
          </Button>
        </OwnerActions>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="flex flex-col gap-1.5">
        <p className="text-sm text-muted-foreground">{OVERVIEW_BLURB}</p>
        <p className="text-sm text-destructive">Initialization failed{error ? `: ${error}` : ""}</p>
        <OwnerActions
          isOwner={isOwner}
          helperText={
            <>
              Use <strong>Retry</strong> to run initialization again.
            </>
          }
        >
          <Button variant="outline" size="sm" loading={isInitializing} onClick={onInitialize}>
            Retry
          </Button>
        </OwnerActions>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-sm text-muted-foreground">{OVERVIEW_BLURB}</p>
      <p className="text-sm text-muted-foreground">Dataset overview has not been initialized.</p>
      <OwnerActions
        isOwner={isOwner}
        helperText={
          <>
            Use <strong>Initialize</strong> to generate it for the first time.
          </>
        }
      >
        <Button variant="outline" size="sm" loading={isInitializing} onClick={onInitialize}>
          Initialize
        </Button>
      </OwnerActions>
    </div>
  );
};

export default InitializationStatusSection;
